import type { CSSProperties, ReactNode } from 'react';

export type AgentPageLayoutProps = {
  children?: ReactNode;
  /** Top area (e.g. back link) */
  before?: ReactNode;
  /** Title — `string` uses standard portal `h1`; `ReactNode` for custom layout */
  title?: ReactNode;
  /** Subtitle below the title */
  description?: ReactNode;
  /** Actions (right-aligned on desktop) */
  actions?: ReactNode;
  className?: string;
  contentClassName?: string;
  contentStyle?: CSSProperties;
};

/**
 * Standard content frame for Agent pages (used inside {@link AppLayout}).
 * Keeps consistent spacing, header row, and typography across pages.
 */
export function AgentPageLayout({
  children,
  before,
  title,
  description,
  actions,
  className = '',
  contentClassName = '',
  contentStyle,
}: AgentPageLayoutProps) {
  const showTitleRow = title != null || description != null || actions != null;

  const titleNode =
    title == null ? null : typeof title === 'string' ? (
      <h1 className="text-xl font-bold text-gray-900">{title}</h1>
    ) : (
      <div className="min-w-0">{title}</div>
    );

  const descriptionNode =
    description == null ? null : typeof description === 'string' ? (
      <p className="mt-1 text-sm text-gray-500">{description}</p>
    ) : (
      <div className="mt-1">{description}</div>
    );

  return (
    <div className={`min-w-0 w-full ${className}`.trim()}>
      {before != null && <div className="mb-4">{before}</div>}
      {showTitleRow && (
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            {titleNode}
            {descriptionNode}
          </div>
          {actions != null && (
            <div className="flex shrink-0 flex-wrap items-start gap-2 sm:justify-end">{actions}</div>
          )}
        </div>
      )}
      <div className={`min-w-0 ${contentClassName}`.trim()} style={contentStyle}>
        {children}
      </div>
    </div>
  );
}
